/**
 * Phase 36 Session 36.1 — billing enums + permission map.
 *
 * Closed-set string unions for the subscription billing surface. Future
 * values land as code-only changes — DB columns are plain varchar, not
 * pg enums (per the 18.4.1 extensibility precedent).
 */

/**
 * Stripe-mirrored subscription status. Mirrors `Stripe.Subscription.Status`
 * verbatim so a webhook handler can pass through without remapping.
 *
 * Today (Phase 36a) the `billing.subscriptions` table stays empty; values
 * are reserved for Phase 36b's webhook upserts and the
 * `SubscriptionActiveGuard` JWT-time check.
 */
export const SUBSCRIPTION_STATUSES = [
  'incomplete',
  'incomplete_expired',
  'trialing',
  'active',
  'past_due',
  'canceled',
  'unpaid',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
export const isSubscriptionStatus = (v: unknown): v is SubscriptionStatus =>
  typeof v === 'string' &&
  (SUBSCRIPTION_STATUSES as readonly string[]).includes(v);

/**
 * Set of statuses that grant the merchant continued platform access.
 * `SubscriptionActiveGuard` (Phase 36b) gates dashboard + POS routes on
 * `organizations.subscription_status` ∈ this set.
 */
export const ACTIVE_SUBSCRIPTION_STATUSES: readonly SubscriptionStatus[] = [
  'trialing',
  'active',
  'past_due',
];

/** Billing cadence the merchant picked at checkout. */
export const BILLING_INTERVALS = ['month', 'year'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];
export const isBillingInterval = (v: unknown): v is BillingInterval =>
  typeof v === 'string' && (BILLING_INTERVALS as readonly string[]).includes(v);

/**
 * Plan codes seeded by `seedBillingPlans`. The marketing site + dashboard
 * resolve plans by code (stable across DB resets); UUIDs ride internally.
 * `'enterprise'` is a contact-sales tier (no self-serve Stripe price — its
 * `stripe_price_id_*` columns stay NULL by design and its features carry
 * `contactSales: true`). Future tiers (e.g. `'free_trial'`) land as code-only
 * changes alongside the seed update.
 */
export const SUBSCRIPTION_PLAN_CODES = [
  'starter',
  'growth',
  'scale',
  'enterprise',
] as const;
export type SubscriptionPlanCode = (typeof SUBSCRIPTION_PLAN_CODES)[number];
export const isSubscriptionPlanCode = (v: unknown): v is SubscriptionPlanCode =>
  typeof v === 'string' &&
  (SUBSCRIPTION_PLAN_CODES as readonly string[]).includes(v);

/**
 * Permission-code constants. Mirrors the 28.11 / 30 / 18.4.1 patterns.
 *
 * Phase 36.3 split the legacy `MANAGE_BILLING` placeholder (which had ZERO
 * runtime consumers — never wired to a `@Permissions()` decorator) into
 * VIEW / MANAGE so a future "billing analyst" role can read subscription
 * state without inheriting portal-launch + plan-change authority.
 *
 * - `VIEW` — read own organization subscription, plan, status, period
 *   dates, and invoice history. Backfilled onto owner; future read-only
 *   accounting roles can earn this without MANAGE.
 * - `MANAGE` — open Stripe Customer Portal, change plan, manage payment
 *   method, and cancel subscription. Owner only at launch.
 * - `MANAGE_SIGNUP_TOKENS` — operator-mint magic-link tokens (reduced 36.2
 *   admin endpoint). Distinct from VIEW/MANAGE because the sales-driven
 *   onboarding workflow is a separate authority surface.
 */
export const BillingPermissions = {
  VIEW: 'billing.view',
  MANAGE: 'billing.manage',
  MANAGE_SIGNUP_TOKENS: 'billing.manage_signup_tokens',
} as const;
export type BillingPermission =
  (typeof BillingPermissions)[keyof typeof BillingPermissions];

// ────────────────────────────────────────────────────────────────────────────
// Suite entitlements (Phase 36 Session 36.10)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Phase 36.10 — product-suite identifiers used for plan-tier entitlement
 * gating on top of the existing per-org / per-location feature flag
 * resolver (Session 21.3).
 *
 * Every `defineFeature(...)` call in the pos-core registry carries one of
 * these via its `suite` field; every `billing.subscription_plans` row
 * declares which suites it unlocks via `entitled_suites text[]`. The
 * resolver in `FeatureFlagService.getFeatureMap()` does a terminal
 * "plan covers this suite?" check before any other layer applies — a
 * feature whose suite isn't in the org's plan resolves to `false`
 * regardless of preset / override / default.
 *
 * `'core'` is a sentinel meaning "always available regardless of plan"
 * (checkout, auth, org settings, terminology, etc.). Every plan tier —
 * including a hypothetical free / trial — implicitly entitles `'core'`.
 *
 * Future suites land as code-only additions here + new entries on every
 * plan's `entitled_suites` array (or a dedicated migration that backfills
 * across existing plans).
 */
export const SUITE_IDS = [
  'core',
  'pos',
  'kds',
  'invoice',
  'payroll',
  'inventory',
  'marketing',
  'reporting',
  // Phase 50 — field service management (jobs, dispatch, work orders,
  // visits, service agreements, job costing) for the `field_services`
  // vertical. Entitled on Scale + Enterprise plans.
  'field_service',
  // Phase 57 — in-house general ledger (chart of accounts, double-entry
  // journals, period close, financial statements). Its own suite rather
  // than a `reporting` feature: reporting summarizes what happened, the
  // ledger is a system of record with its own close and audit posture.
  'accounting',
  // ── Phase 64 — plan packaging matches the pricing cards ──────────────
  // (user decision 2026-09-28: gate the features, don't re-tier the
  // bullets). One suite per card capability, so moving a bullet between
  // tiers is a data change on `entitled_suites`, never a code change.
  // These names are persisted in `billing.subscription_plans`; never
  // rename one — add a new suite and migrate instead.
  //
  // Growth "Floor plan editor + table management" + "Bar tabs with Stripe
  // Terminal pre-auth". Coursing, seats and split checks stay in `pos`.
  'table_service',
  // Growth "Loyalty program + tiered rewards".
  'loyalty',
  // Growth "Tax engine (multi-jurisdiction)": jurisdiction rates, tax-code
  // assignment, exemption certificates, the jurisdiction tax report.
  // Location-rate calculation at checkout is `core` and never gated.
  'tax_engine',
  // Growth "Online ordering surface": the storefront + QR ordering.
  'online_ordering',
  // Scale "Reservations + Waitlist + Unified Host Stand" (+ online booking).
  // A service provider's appointment book runs on the same reservation API;
  // it stays open on every plan while `pos.service_appointment` is on.
  'reservations',
  // Scale "Recipe costing + menu engineering".
  'recipe_costing',
  // Scale "AI benchmarking — rank against anonymized peers".
  'benchmarking',
  // Scale "Reporting API + custom export pipelines": the public API's
  // report reads and outbound webhook pipelines.
  'reporting_api',
  // Scale "Custom domain for your storefront".
  'custom_domains',
  // Scale "Audit log + GDPR-compliant data export": the org-wide audit
  // log, its export and the compliance reviews. A person's own data export
  // and erasure stay open on every plan — those are legal duties.
  'audit_log',
  // Enterprise "SSO / SAML": configuring SSO + SCIM. Sign-in through an
  // existing connection is never gated.
  'sso',
] as const;
export type SuiteId = (typeof SUITE_IDS)[number];
export const isSuiteId = (v: unknown): v is SuiteId =>
  typeof v === 'string' && (SUITE_IDS as readonly string[]).includes(v);

/**
 * Phase 64 — merchant-facing suite names, for "not included in your plan"
 * notices. A `Record` over `SuiteId` so adding a suite without a label is
 * a type error rather than a raw id on screen.
 */
export const SUITE_LABELS: Readonly<Record<SuiteId, string>> = {
  core: 'Core',
  pos: 'Point of sale',
  kds: 'Kitchen display',
  invoice: 'Invoicing',
  payroll: 'Scheduling + payroll',
  inventory: 'Inventory',
  marketing: 'Marketing',
  reporting: 'AI + analytics',
  field_service: 'Field service',
  accounting: 'General ledger',
  table_service: 'Table service',
  loyalty: 'Loyalty',
  tax_engine: 'Tax engine',
  online_ordering: 'Online ordering',
  reservations: 'Reservations',
  recipe_costing: 'Recipe costing',
  benchmarking: 'AI benchmarking',
  reporting_api: 'Reporting API',
  custom_domains: 'Custom domains',
  audit_log: 'Audit log',
  sso: 'SSO / SAML',
};

/**
 * Suites that are ALWAYS entitled regardless of subscription plan. The
 * `'core'` suite (checkout, auth, org settings) is the only entry today.
 *
 * The server resolver still reads the org's plan + override rows on every
 * cache miss (single 60s-cached `load(orgId)` call) — this floor is layered
 * onto the plan's `entitled_suites` by `resolveEntitledSuites` so a feature
 * mapped to `'core'` always resolves entitled regardless of plan tier, even
 * when the org has no active subscription at all.
 */
export const ALWAYS_ENTITLED_SUITES: readonly SuiteId[] = ['core'];

/**
 * Computes the merchant's effective entitled-suites set given the plan's
 * declared suites + the always-entitled floor. De-dupes via Set so
 * `entitled_suites` containing `'core'` (legal but redundant) doesn't
 * surface twice. Order: always-entitled first, then plan-declared in
 * insertion order. Pure helper — used by both server resolver + client
 * dashboard matrix to stay in lockstep.
 */
export function resolveEntitledSuites(
  planEntitledSuites: readonly SuiteId[] | null | undefined,
): SuiteId[] {
  const result: SuiteId[] = [...ALWAYS_ENTITLED_SUITES];
  const seen = new Set<SuiteId>(ALWAYS_ENTITLED_SUITES);
  for (const suite of planEntitledSuites ?? []) {
    if (!isSuiteId(suite) || seen.has(suite)) continue;
    result.push(suite);
    seen.add(suite);
  }
  return result;
}

/**
 * Server-side mirror of pos-core's `featureRegistry` (id → suite). Phase 36.10
 * uses this map to decide whether a feature ID falls inside the org's entitled
 * suites. The api can't import pos-core directly (rn-shared / zustand peer-dep
 * transitive graph + src-direct-only build with no `dist/`), so we duplicate
 * the {id, suite} subset here.
 *
 * Drift contract: when a feature is added to pos-core's `featureRegistry.ts`,
 * mirror it here too (and in `FEATURE_DEFAULT_MAP` below). The cost of
 * forgetting is small — an unmapped feature ID skips the entitlement gate and
 * falls through the standard precedence chain (preset → default), so a
 * Starter merchant could see the new feature without upgrading. Drift is now
 * machine-checked: `packages/pos-core/src/featureRegistry.spec.ts` asserts
 * both maps stay in lockstep with the registry, so `pnpm --filter
 * @commerce-os/pos-core test` fails the moment they diverge.
 */
export const FEATURE_SUITE_MAP: Readonly<Record<string, SuiteId>> = {
  'pos.checkout': 'core',
  'pos.modifiers': 'pos',
  'pos.quick_keys': 'pos',
  'pos.open_item': 'pos',
  // Phase 64 — the Growth card sells both; they rode `pos` (every plan).
  'pos.bar_tabs': 'table_service',
  'pos.floor_plan': 'table_service',
  'pos.gift_cards': 'pos',
  'pos.register': 'pos',
  'pos.split_tender': 'pos',
  // Phase 51.6 — per-course fire/hold.
  'pos.coursing': 'pos',
  // Phase 52.2 — item-to-seat + split-by-seat.
  'pos.seat_ordering': 'pos',
  // Phase 52.3 — persisted split checks.
  'pos.split_checks': 'pos',
  // Phase 52.4 — combo/bundle products.
  'pos.combos': 'pos',
  // Phase 52.5 — time-based price rules.
  'pos.price_rules': 'pos',
  'pos.barcode_first': 'pos',
  'pos.weight_pricing': 'pos',
  'pos.plu_at_scale': 'pos',
  'pos.age_verification': 'pos',
  'pos.quick_tender': 'pos',
  'pos.lottery_payout': 'pos',
  // Phase 44.2 — dual pricing / surcharging.
  'pos.dual_pricing': 'pos',
  // Phase 44.3 — EBT/SNAP tender.
  'pos.ebt_snap': 'pos',
  // Phase 44.5 — Tap to Pay on iPhone/Android.
  'pos.tap_to_pay': 'pos',
  'pos.compact_checkout': 'pos',
  'pos.customer_facing_tip': 'pos',
  'pos.pump_integration': 'pos',
  'pos.service_appointment': 'pos',
  'pos.tip_per_line': 'pos',
  // Phase 64 — the public booking page is part of the Scale reservations
  // bullet; `pos.reservations` is the staff side (host stand, waitlist).
  'pos.online_booking': 'reservations',
  'pos.reservations': 'reservations',
  // Phase 64 — Growth "Loyalty program + tiered rewards".
  'pos.loyalty': 'loyalty',
  // Phase 64 — Growth "Online ordering surface" (storefront + QR ordering).
  'pos.online_ordering': 'online_ordering',
  // Phase 65.2 — carrier shipping for online orders, on the merchant's own
  // EasyPost account. Which plan includes it is 65.7's decision.
  'pos.shipping': 'online_ordering',
  // Phase 66.6 — bring-your-own Stripe is on every plan (decision 2); `core` is every plan's.
  'payments.byo_stripe': 'core',
  'messaging.customer_texts': 'core',
  // Phase 64 — Scale "Custom domain for your storefront".
  'pos.custom_domain': 'custom_domains',
  'pos.customers': 'pos',
  'pos.offline_mode': 'core',
  'pos.kiosk_self_service': 'pos',
  'pos.customer_facing_display': 'pos',
  // Phase 64 — tableside ordering picks a floor-plan table and orders on
  // its session; it cannot work without the floor plan, so it rides with it.
  'pos.handheld_mode': 'table_service',
  'pos.menu_upsells': 'pos',
  'pos.online_ordering_snooze': 'pos',
  'pos.shift_logbook': 'pos',
  'pos.broadcast_messages': 'pos',
  // Phase 33.3 — voice ordering. Was missing from this map until the
  // drift-guard spec landed; mapping it brings the entitlement gate in line
  // with the registry's declared suite.
  'pos.voice_ordering': 'pos',
  'kds.stations': 'kds',
  'kds.printer_tickets': 'kds',
  // Phase 64 — Growth "Tax engine (multi-jurisdiction)". Hides the
  // tax-code, exemption and jurisdiction-report surfaces; checkout keeps
  // charging whatever is configured either way.
  'tax.multi_jurisdiction': 'tax_engine',
  'invoice.quotes': 'invoice',
  // Phase 44.4b — house accounts (charge-to-account AR).
  'invoice.house_accounts': 'invoice',
  // Basic time clock is `core` (available on every plan); advanced payroll
  // (scheduling + runs) is gated at the API by @RequiresSuite('payroll').
  'payroll.time_clock': 'core',
  // Phase 47 — Employee Self-Service in the POS apps. `core` like the
  // time clock (schedule reads still carry @RequiresSuite('payroll')
  // server-side); pay visibility is a merchant opt-in flag on `core` so
  // integration-only orgs don't need the payroll suite.
  'payroll.ess': 'core',
  'payroll.ess_pay': 'core',
  // Phase 48.2 — ESS open-shift claims ride the payroll suite (claim
  // endpoints carry @RequiresSuite('payroll')).
  'payroll.open_shifts': 'payroll',
  'inventory.tracking': 'inventory',
  // Phase 56 — inventory depth. Cycle counts + invoice scanning ride the
  // same `inventory` suite as tracking (they're depth, not a new tier).
  'inventory.cycle_counts': 'inventory',
  'inventory.invoice_scanning': 'inventory',
  // Phase 64 — Scale "Recipe costing + menu engineering".
  'inventory.recipe_costing': 'recipe_costing',
  'accounting.general_ledger': 'accounting',
  'marketing.campaigns': 'marketing',
  'reporting.ai_write_actions': 'reporting',
  // Phase 46.2 — per-org kill-switch for the conversational AI assistant.
  // Rides the `reporting` suite (Growth+ — migration 0096 deliberately made
  // AI Growth+; don't re-gate to Scale).
  'analytics.assistant': 'reporting',
  // Phase 64 — Scale "AI benchmarking" (the rest of /analytics is Growth).
  'analytics.benchmarking': 'benchmarking',
  // Phase 64 — Scale "Audit log + GDPR-compliant data export".
  'audit.log': 'audit_log',
  // Phase 64 — Enterprise "SSO / SAML".
  'auth.sso': 'sso',
  // Phase 50 — field service management suite. All flags ride the
  // `field_service` suite; the `field_services` vertical preset turns the
  // first nine on by default (crm + alerts stay opt-in).
  'field_service.core': 'field_service',
  'field_service.quoting': 'field_service',
  'field_service.visits': 'field_service',
  'field_service.dispatch': 'field_service',
  'field_service.work_orders': 'field_service',
  'field_service.change_orders': 'field_service',
  'field_service.agreements': 'field_service',
  'field_service.media': 'field_service',
  'field_service.costing': 'field_service',
  'field_service.crm': 'field_service',
  'field_service.alerts': 'field_service',
};

/**
 * Returns the suite a feature ID belongs to, or `undefined` for unregistered
 * features. Mirrors pos-core's `getFeature(id)?.suite` without dragging the
 * full registry into the server bundle.
 */
export function getFeatureSuite(featureId: string): SuiteId | undefined {
  return FEATURE_SUITE_MAP[featureId];
}

/**
 * Server-side mirror of pos-core's `featureRegistry` (id → registry default).
 * The server's `FeatureFlagService` resolver historically bottomed out at
 * `false` for features with no override row and no vertical-preset hit — but
 * the client resolver (`pos-core#isFeatureEnabled`) bottoms out at the
 * registry default. For default-ON features absent from every preset (e.g.
 * `analytics.assistant`), that split meant the UI showed the feature while
 * server-side gates 403'd it. This map lets the server land on the same
 * answer the client does.
 *
 * Same drift contract as `FEATURE_SUITE_MAP` above, enforced by the same
 * spec (`packages/pos-core/src/featureRegistry.spec.ts`).
 */
export const FEATURE_DEFAULT_MAP: Readonly<Record<string, boolean>> = {
  'pos.checkout': true,
  'pos.modifiers': true,
  'pos.quick_keys': true,
  'pos.open_item': true,
  'pos.bar_tabs': false,
  'pos.floor_plan': false,
  'pos.gift_cards': true,
  'pos.register': true,
  'pos.split_tender': true,
  // Phase 51.6 — off by default; restaurant preset enables it.
  'pos.coursing': false,
  // Phase 52.2 — off by default; restaurant preset enables it.
  'pos.seat_ordering': false,
  // Phase 52.3 — off by default; restaurant preset enables it.
  'pos.split_checks': false,
  // Phase 52.4 — off by default; restaurant/bar/food-truck presets enable it.
  'pos.combos': false,
  // Phase 52.5 — off by default; restaurant/bar presets enable it.
  'pos.price_rules': false,
  'pos.barcode_first': false,
  'pos.weight_pricing': false,
  'pos.plu_at_scale': false,
  'pos.age_verification': false,
  'pos.quick_tender': false,
  'pos.lottery_payout': false,
  'pos.dual_pricing': false,
  'pos.ebt_snap': false,
  'pos.tap_to_pay': false,
  'pos.compact_checkout': false,
  'pos.customer_facing_tip': false,
  'pos.pump_integration': false,
  'pos.service_appointment': false,
  'pos.tip_per_line': false,
  'pos.online_booking': false,
  // Phase 64 — default ON: these surfaces were ungated for every plan, so
  // an entitled org keeps exactly what it had; the plan lock is what
  // turns them off below Growth / Scale.
  'pos.reservations': true,
  'pos.loyalty': true,
  'pos.online_ordering': true,
  // Phase 65.2 — off until the merchant chooses to ship.
  'pos.shipping': false,
  'payments.byo_stripe': true,
  'messaging.customer_texts': false,
  'pos.custom_domain': true,
  'pos.customers': false,
  'pos.offline_mode': true,
  'pos.kiosk_self_service': false,
  'pos.customer_facing_display': false,
  'pos.handheld_mode': false,
  'pos.menu_upsells': false,
  'pos.online_ordering_snooze': false,
  'pos.shift_logbook': false,
  'pos.broadcast_messages': false,
  'pos.voice_ordering': false,
  'kds.stations': false,
  'kds.printer_tickets': false,
  'tax.multi_jurisdiction': true,
  'invoice.quotes': true,
  'invoice.house_accounts': false,
  'payroll.time_clock': true,
  'payroll.ess': true,
  'payroll.ess_pay': false,
  'payroll.open_shifts': true,
  'inventory.tracking': true,
  // Phase 56 — invoice_scanning defaults ON: with no VLM configured it
  // null-degrades to an honest failed scan (stub-on-not-configured).
  'inventory.cycle_counts': true,
  'inventory.invoice_scanning': true,
  'inventory.recipe_costing': true,
  // Phase 57 — OFF by default. Unlike most flags this one starts a set
  // of books: turning it on begins posting journals an operator is then
  // responsible for. That has to be a deliberate act, not a default.
  'accounting.general_ledger': false,
  'marketing.campaigns': true,
  'reporting.ai_write_actions': false,
  'analytics.assistant': true,
  // Phase 64 — default ON like the surfaces they replace (all were
  // reachable on every plan); the plan lock turns them off below the
  // tier that sells them.
  'analytics.benchmarking': true,
  'audit.log': true,
  'auth.sso': true,
  // Phase 50 — field service. Every flag defaults OFF; the `field_services`
  // vertical preset is what turns the suite on for a fresh field-service org.
  'field_service.core': false,
  'field_service.quoting': false,
  'field_service.visits': false,
  'field_service.dispatch': false,
  'field_service.work_orders': false,
  'field_service.change_orders': false,
  'field_service.agreements': false,
  'field_service.media': false,
  'field_service.costing': false,
  'field_service.crm': false,
  'field_service.alerts': false,
};

/**
 * Returns the registry default for a feature ID, or `undefined` for
 * unregistered features. Mirrors pos-core's `getFeature(id)?.default`.
 */
export function getFeatureDefault(featureId: string): boolean | undefined {
  return FEATURE_DEFAULT_MAP[featureId];
}
